import { gql } from '@apollo/client';
import { SlackInstallationFieldsFragment } from './slack/fragments';

export const UserSettingFieldsFragment = gql`
  fragment UserSettingFields on UserSetting {
    kind
    value
  }
`;

export const CreatorFields = gql`
  fragment CreatorFields on User {
    id
    name
    email
    picture
  }
`;

export const EditorFields = gql`
  fragment EditorFields on User {
    id
    name
    email
    picture
  }
`;

export const UserFragment = gql`
  fragment UserFields on User {
    id
    email
    name
    picture
    lastLogin
    createdAt
    state
    defaultSchemaSetting {
      ...UserSettingFields
    }
    defaultViewSetting {
      ...UserSettingFields
    }
    quickAccessDictionarySetting {
      ...UserSettingFields
    }
    slackInstallation {
      ...SlackInstallationFields
    }
  }
  ${UserSettingFieldsFragment}
  ${SlackInstallationFieldsFragment}
`;

export const PageInfoFragment = gql`
  fragment PageInfoFields on PageInfo {
    total
    last
    hasMore
    first
  }
`;

export const ErrorFragment = gql`
  fragment ErrorFields on Error {
    field
    messages
  }
`;

export const SubjectFragment = gql`
  fragment SubjectFields on Subject {
    key
    name
  }
`;

export const SchemaFieldsFragment = gql`
  fragment SchemaFields on Schema {
    id
    name
    supportsAnyEvent
    disabled
    latestDataAt
    description
    requiredPermission
    isInternal
  }
`;

export const LogicalOperatorFragment = gql`
  fragment LogicalOperatorFields on LogicalOperator {
    key
    name
  }
`;

export const MeasureFunctionFragment = gql`
  fragment MeasureFunctionFields on MeasureFunction {
    key
    name
    fieldRequired
    requirePercentile
    forceBreakdownOverall
  }
`;

export const RelationalOperatorFragment = gql`
  fragment RelationalOperatorFields on RelationalOperator {
    key
    name
    values
    supportCaseInsensitive
  }
`;

export const UserAliasQueryMeasureFragment = gql`
  fragment UserAliasQueryMeasureFields on UserAliasQueryMeasure {
    aliasId
  }
`;

export const VersionFieldsFragment = gql`
  fragment VersionFields on Version {
    baseId
    comment
    createdAt
    creator {
      ...CreatorFields
    }
    editor {
      ...EditorFields
    }
    editedAt
    description
    entityId
    isDisabled
    isLatestVersion
    name
    version
    versionId
  }
  ${CreatorFields}
  ${EditorFields}
`;

export const DateRangeFieldsFragment = gql`
  fragment DateRangeFields on DateRange {
    start
    end
  }
`;

export const PartitionFieldsFragment = gql`
  fragment PartitionFields on Partition {
    ... on NonePartition {
      key
      name
    }
    ... on FieldPartition {
      key
      name
      field {
        type
      }
    }
    ... on ConversionWindowPartition {
      key
      name
    }
  }
`;

export const PartitionValueFieldsFragment = gql`
  fragment PartitionValueFields on PartitionValue {
    partition {
      ...PartitionFields
    }
    conversionWindow {
      value
      timeUnit
    }
  }
  ${PartitionFieldsFragment}
`;

export const UserAliasReplacementFieldsFragment = gql`
  fragment UserAliasReplacementFields on UserAliasReplacement {
    aliasId
    replacement
  }
`;

export const EntityRoleAssignmentFieldsFragment = gql`
  fragment EntityRoleAssignmentFields on EntityRoleAssignment {
    groups {
      id
    }
    role
    users {
      id
      email
      name
      picture
    }
  }
`;

export const EntityPermissionFieldsFragment = gql`
  fragment EntityPermissionFields on EntityPermission {
    defaultPublicRole
    entityId
    entityType
    roleAssignments {
      ...EntityRoleAssignmentFields
    }
    owner {
      ...CreatorFields
    }
  }
  ${EntityRoleAssignmentFieldsFragment}
  ${CreatorFields}
`;

export const SchemaConfigFieldsFragment = gql`
  fragment SchemaConfigFields on SchemaConfig {
    schemas {
      ...SchemaFields
    }
    defaultSchemaId
    allSchemas {
      ...SchemaFields
    }
  }
  ${SchemaFieldsFragment}
`;

export const PermissionConfigFieldsFragment = gql`
  fragment PermissionConfigFields on PermissionConfig {
    enablePermissionControl
    schemas {
      id
      name
      requiredPermission
    }
    allSchema {
      id
      name
    }
  }
`;

export const CustomerInfoFieldsFragment = gql`
  fragment CustomerInfoFields on CustomerInfo {
    name
    analyticsConfig {
      enabled
      segment {
        writeKey
      }
    }
    createdAt
    warehouse
  }
`;

const DatePickerFixedFieldsFragment = gql`
  fragment DatePickerFixedFields on DatePickerFixed {
    start
    end
  }
`;

const DatePickerLastFieldsFragment = gql`
  fragment DatePickerLastFields on DatePickerLast {
    period
    unit
  }
`;

const DatePickerLastFullFieldsFragment = gql`
  fragment DatePickerLastFullFields on DatePickerLastFull {
    unit
    period
  }
`;

const DatePickerSinceFieldsFragment = gql`
  fragment DatePickerSinceFields on DatePickerSince {
    start
  }
`;

const DatePickerStartOfFieldsFragment = gql`
  fragment DatePickerStartOfFields on DatePickerStartOf {
    unit
  }
`;

export const DatePickerFieldsFragment = gql`
  fragment DatePickerFields on DatePicker {
    ... on DatePickerFixed {
      ...DatePickerFixedFields
    }
    ... on DatePickerSince {
      ...DatePickerSinceFields
    }
    ... on DatePickerStartOf {
      ...DatePickerStartOfFields
    }
    ... on DatePickerLastFull {
      ...DatePickerLastFullFields
    }
    ... on DatePickerLast {
      ...DatePickerLastFields
    }
  }
  ${DatePickerFixedFieldsFragment}
  ${DatePickerLastFieldsFragment}
  ${DatePickerLastFullFieldsFragment}
  ${DatePickerSinceFieldsFragment}
  ${DatePickerStartOfFieldsFragment}
`;

export const UserAliasQueryStepFieldsFragment = gql`
  fragment UserAliasQueryStepFields on UserAliasQueryStep {
    aliasId
  }
`;

export const FunnelStepBriefInfoFields = gql`
  fragment FunnelStepBriefInfoFields on FunnelStep {
    name
    seq
    userAlias {
      ...UserAliasQueryStepFields
    }
  }
  ${UserAliasQueryStepFieldsFragment}
`;

export const FunnelModeSliceFieldsFragment = gql`
  fragment FunnelModeSliceFields on FunnelModeSlice {
    name
    key
    measureSelection
  }
`;

const AnalysisDashboardContainerFieldsFragment = gql`
  fragment AnalysisDashboardContainerFields on AnalysisDashboardContainer {
    id
    baseId
    name
    isLatestInstance
    isDefault
    permission {
      ...EntityPermissionFields
    }
  }
  ${EntityPermissionFieldsFragment}
`;

const AnalysisWorkspaceContainerFieldsFragment = gql`
  fragment AnalysisWorkspaceContainerFields on AnalysisWorkspaceContainer {
    name
    baseId
  }
  ${EntityPermissionFieldsFragment}
`;

export const AnalysisContainerFieldsFragment = gql`
  fragment AnalysisContainerFields on AnalysisContainer {
    ... on AnalysisDashboardContainer {
      ...AnalysisDashboardContainerFields
    }
    ... on AnalysisWorkspaceContainer {
      ...AnalysisWorkspaceContainerFields
    }
  }
  ${AnalysisDashboardContainerFieldsFragment}
  ${AnalysisWorkspaceContainerFieldsFragment}
`;
